import { AxiosPromise, AxiosResponse } from 'axios'
import { http } from '../http/http'
import { UserPermissions } from '../models/permissions'
import { Store, User, ProfileData } from '../models/user'

/**
 * @export UserApi - Just an api interface for claiming user data and reducing the code needed for axios http functions
 * @class UserApi
 */
export class UserApi {
    /**
     * Base Url for User Api
     * @static
     * @type {string}
     * @memberof UserApi
     */
    static USER_BASE_URL: string = process.env.DIGITAL_PORTAL_URL_BE
    static SMARTLY_BASE_URL: string = process.env.SMARTLY_BASE_URL_BE

    /**
     * Endpoint for user info
     * @static
     * @type {string}
     * @memberof UserApi
     */
    static USER_WHOAMI_ENDP: string = '/whoami'
    static USER_PROFILE_INFO_ENDP: string = '/profileapi/user/profile?whoamiData=true'
    static USER_LOGOUT_ENDP: string = '/profileapi/trace_event?type=logout'
    static USER_APP_PERM_ENDP: string = '/portals/web'
    static USER_STORE: string = '/store'

    static getApiUrl(url: string, baseUrl: string = UserApi.USER_BASE_URL) {
        return `${baseUrl}${url}`
    }

    /**
     * This functions execute a logout for the current user
     * @returns Promise of type AxiosPromise
     */
    static Logout = async (
        token?: string,
        baseUrl: string = UserApi.SMARTLY_BASE_URL
    ): Promise<AxiosPromise<any>> => {
        try {
            if (token) {
                let response = await http.get<any>(
                    UserApi.getApiUrl(UserApi.USER_LOGOUT_ENDP, baseUrl)
                )
                return response
            } else {
                return
            }
        } catch (error) {
            throw error
        }
    }
    /**
     * This functions gets the user info
     * @returns Promise of type AxiosPromise
     */
    static WhoAmI = async (
        baseUrl?: string
    ): Promise<AxiosPromise<IResponseWhoAmI>> => {
        try {
            let response = await http.get<IResponseWhoAmI>(
                UserApi.getApiUrl(UserApi.USER_WHOAMI_ENDP, baseUrl),
                {
                    params: {
                        business_function: true,
                    },
                }
            )
            return response
        } catch (error) {
            console.log(
                'Error on WhoAmI',
                error,
                'error.response',
                error.response,
                'error.response.status',
                error.response.status
            )
            if (error.response.status !== 401)
                window.location.href =
                    process.env.DIGITAL_PORTAL_URL + '/login_error_whoami'
        }
    }

    /**
     * This functions gets extra profile info such as selected language and avatar pic
     * @returns Promise of type AxiosPromise
     */
    static ProfileInfo = async (
        baseUrl: string = UserApi.SMARTLY_BASE_URL
    ): Promise<AxiosPromise<IResponseProfileInfo>> => {
        try {
            let response = await http.get<IResponseProfileInfo>(
                UserApi.getApiUrl(UserApi.USER_PROFILE_INFO_ENDP, baseUrl),
                {
                    headers: {
                        'Accept-Language': '',
                    },
                    timeout: 180000,
                }
            )
            return response
        } catch (error) {
            console.log(
                'Error on UserInfo',
                error,
                'error.response',
                error.response,
                'error.response.status',
                error.response.status
            )
        }
    }

    /**
     * Get the app permissions list needed by the sidebar
     * @returns Promise of type AxiosPromise
     */
    static GetEnabledApps = async (
        baseUrl?: string
    ): Promise<AxiosPromise<UserPermissions>> => {
        try {
            let response = await http.get<UserPermissions>(
                UserApi.getApiUrl(UserApi.USER_APP_PERM_ENDP, baseUrl)
            )
            return response
        } catch (error) {
            console.log(
                'Error on GetEnabledApps',
                error,
                'error.response',
                error.response,
                'error.response.status',
                error.response.status
            )
            if (error.response.status !== 401)
                window.location.href =
                    process.env.DIGITAL_PORTAL_URL + '/login_error_enabledapps'
        }
    }
    /**
     * Get the user store if user have one
     * @returns Promise of type AxiosPromise
     */
    static getUserStore = async (
        baseUrl?: string
    ): Promise<AxiosPromise<IResponseUserStore>> => {
        try {
            let response = await http.get<IResponseUserStore>(
                UserApi.getApiUrl(UserApi.USER_STORE, baseUrl)
            )
            return response
        } catch (error) {
            console.log(
                'Error on getUserStore',
                error,
                'error.response',
                error.response,
                'error.response.status',
                error.response.status
            )
            if (error.response.status !== 401)
                window.location.href =
                    process.env.DIGITAL_PORTAL_URL + '/login_error_userstore'
        }
    }
}

export interface IResponseWhoAmI {
    user: User
}
export interface IResponseProfileInfo {
    user: ProfileData
}
export interface IResponseUserStore {
    store: Store
}
